import { useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { formatApiErrorDetail } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { X } from '@phosphor-icons/react';

const API = '/api';

const COMMON_CERTIFICATIONS = [
  "ICAO Annex 13 Investigation",
  "Aircraft Accident Investigation (AAI)",
  "ISASI Member",
  "Human Factors in Aviation Safety",
  "Flight Data Recorder Analysis",
  "Cockpit Voice Recorder Analysis",
  "Aircraft Structures & Metallurgy",
  "Aircraft Powerplant Investigation",
  "Aircraft Systems Investigation",
  "Cabin Safety Investigation",
  "Witness Interview Techniques",
  "Survival Factors Investigation",
  "Fire & Explosion Investigation",
  "Marine Casualty Investigation",
  "IMO Marine Investigation (Code)",
  "Ship Structural Failure Analysis",
  "Maritime Human Factors",
  "Port Safety Management",
  "Dangerous Goods Investigation",
  "Safety Management Systems (SMS) Auditor",
  "ICAO SMS Instructor",
  "Drone/UAS Investigation",
];

export function passwordProblem(password) {
  if (password.length < 10) return 'Password must be at least 10 characters';
  if (!/[A-Za-z]/.test(password) || !/[0-9]/.test(password)) return 'Password must contain both letters and numbers';
  return null;
}

export default function Register() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'investigator',
    sector: 'aviation',
    phone: '',
    designation: '',
    organization: '',
    years_of_experience: 0,
    investigations_completed: 0,
    certifications: [],
    primary_specialty: '',
    qualifications: ''
  });
  const [certInput, setCertInput] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const availableCerts = useMemo(() =>
    COMMON_CERTIFICATIONS.filter(c => !formData.certifications.includes(c)).slice(0, 8),
    [formData.certifications]
  );

  const addCertification = (cert) => {
    const trimmed = cert.trim();
    if (trimmed && !formData.certifications.includes(trimmed)) {
      setFormData({ ...formData, certifications: [...formData.certifications, trimmed] });
    }
    setCertInput('');
  };

  const removeCertification = (cert) => {
    setFormData({ ...formData, certifications: formData.certifications.filter(c => c !== cert) });
  };

  const filteredSuggestions = certInput.length >= 2
    ? COMMON_CERTIFICATIONS.filter(c => c.toLowerCase().includes(certInput.toLowerCase()) && !formData.certifications.includes(c))
    : [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    const pwProblem = passwordProblem(formData.password);
    if (pwProblem) {
      setError(pwProblem);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        sector: formData.sector,
        phone: formData.phone,
        designation: formData.designation,
        organization: formData.organization,
        years_of_experience: parseInt(formData.years_of_experience) || 0,
        investigations_completed: parseInt(formData.investigations_completed) || 0,
        certifications: formData.certifications,
        primary_specialty: formData.primary_specialty,
        qualifications: formData.qualifications
      };
      const { data } = await axios.post(`${API}/auth/register`, payload, { withCredentials: true });
      alert(data.message || 'Registration successful! Please wait for admin approval.');
      navigate('/login');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <div className="w-full max-w-lg">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Logo */}
          <div className="flex flex-col items-center mb-6">
            <img src="/roottrace-logo-light.png" alt="RootTrace" className="w-56 h-auto object-contain mb-3" />
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Create Account</h1>
          </div>

          {/* Step indicator */}
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'}`}>1</div>
            <div className={`w-12 h-0.5 ${step >= 2 ? 'bg-blue-600' : 'bg-slate-200'}`} />
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'}`}>2</div>
            <div className={`w-12 h-0.5 ${step >= 3 ? 'bg-blue-600' : 'bg-slate-200'}`} />
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${step >= 3 ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'}`}>3</div>
          </div>
          <div className="flex justify-between text-[10px] text-slate-500 mb-6 px-1">
            <span>Account</span>
            <span>Professional</span>
            <span>Certifications</span>
          </div>

          <form onSubmit={handleSubmit} data-testid="register-form">
            {/* Step 1: Account Details */}
            {step === 1 && (
              <div className="space-y-4">
                <div>
                  <Label>Full Name *</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Enter your full name" className="rounded-lg" data-testid="register-name" required />
                </div>
                <div>
                  <Label>Email Address *</Label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="Enter your email" className="rounded-lg" data-testid="register-email" required />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+91 98765 43210" className="rounded-lg" data-testid="register-phone" />
                </div>
                <div>
                  <Label>Requested Role <span className="text-slate-400 font-normal">(confirmed by administrator)</span></Label>
                  <Select value={formData.role} onValueChange={(val) => setFormData({ ...formData, role: val })}>
                    <SelectTrigger className="rounded-lg" data-testid="register-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="investigator">Investigator</SelectItem>
                      <SelectItem value="iic">Investigator in Charge (IIC)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Sector</Label>
                  <Select value={formData.sector} onValueChange={(val) => setFormData({ ...formData, sector: val })}>
                    <SelectTrigger className="rounded-lg" data-testid="register-sector">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="aviation">Aviation</SelectItem>
                      <SelectItem value="maritime">Maritime</SelectItem>
                      <SelectItem value="both">Both</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Password *</Label>
                    <Input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder="Min 10 characters, letters and numbers" className="rounded-lg" data-testid="register-password" required />
                  </div>
                  <div>
                    <Label>Confirm Password *</Label>
                    <Input type="password" value={formData.confirmPassword} onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })} placeholder="Re-enter password" className="rounded-lg" data-testid="register-confirm-password" required />
                  </div>
                </div>
                <Button type="button" onClick={() => {
                  if (!formData.name || !formData.email || !formData.password || !formData.confirmPassword) {
                    setError('Please fill all required fields'); return;
                  }
                  if (formData.password !== formData.confirmPassword) {
                    setError('Passwords do not match'); return;
                  }
                  const pwProblem = passwordProblem(formData.password);
                  if (pwProblem) { setError(pwProblem); return; }
                  setError(''); setStep(2);
                }} className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg h-11" data-testid="next-step-1">
                  Next: Professional Details
                </Button>
              </div>
            )}

            {/* Step 2: Professional Details */}
            {step === 2 && (
              <div className="space-y-4">
                <div>
                  <Label>Designation / Title</Label>
                  <Input value={formData.designation} onChange={(e) => setFormData({ ...formData, designation: e.target.value })} placeholder="e.g., Senior Investigator, Safety Analyst" className="rounded-lg" data-testid="register-designation" />
                </div>
                <div>
                  <Label>Organization</Label>
                  <Input value={formData.organization} onChange={(e) => setFormData({ ...formData, organization: e.target.value })} placeholder="e.g., AAIB, NTSB, DGCA" className="rounded-lg" data-testid="register-organization" />
                </div>
                <div>
                  <Label>Primary Specialty</Label>
                  <Select value={formData.primary_specialty} onValueChange={(val) => setFormData({ ...formData, primary_specialty: val })}>
                    <SelectTrigger className="rounded-lg" data-testid="register-specialty">
                      <SelectValue placeholder="Select your primary area" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Operations">Operations</SelectItem>
                      <SelectItem value="Flight Recorders">Flight Recorders</SelectItem>
                      <SelectItem value="Structures">Structures</SelectItem>
                      <SelectItem value="Powerplants">Powerplants</SelectItem>
                      <SelectItem value="Systems">Systems</SelectItem>
                      <SelectItem value="Human Factors">Human Factors</SelectItem>
                      <SelectItem value="Survivability">Survivability</SelectItem>
                      <SelectItem value="Cabin Safety">Cabin Safety</SelectItem>
                      <SelectItem value="Maintenance">Maintenance</SelectItem>
                      <SelectItem value="ATS & Airports">ATS & Airports</SelectItem>
                      <SelectItem value="Meteorology">Meteorology</SelectItem>
                      <SelectItem value="Witness Statements">Witness Statements</SelectItem>
                      <SelectItem value="Site Survey">Site Survey</SelectItem>
                      <SelectItem value="Photo/Video">Photo/Video Documentation</SelectItem>
                      <SelectItem value="Maritime Operations">Maritime Operations</SelectItem>
                      <SelectItem value="Vessel Structures">Vessel Structures</SelectItem>
                      <SelectItem value="Maritime Propulsion">Maritime Propulsion</SelectItem>
                      <SelectItem value="Navigation Systems">Navigation Systems</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Years of Experience</Label>
                    <Input type="number" min="0" max="50" value={formData.years_of_experience} onChange={(e) => setFormData({ ...formData, years_of_experience: e.target.value })} className="rounded-lg" data-testid="register-experience" />
                  </div>
                  <div>
                    <Label>Investigations Completed</Label>
                    <Input type="number" min="0" value={formData.investigations_completed} onChange={(e) => setFormData({ ...formData, investigations_completed: e.target.value })} className="rounded-lg" data-testid="register-inv-count" />
                  </div>
                </div>
                <div>
                  <Label>Qualifications / Notes</Label>
                  <Textarea value={formData.qualifications} onChange={(e) => setFormData({ ...formData, qualifications: e.target.value })} placeholder="e.g., MSc Aerospace Engineering, FAA Licensed A&P, etc." className="rounded-lg min-h-[70px]" data-testid="register-qualifications" />
                </div>
                <div className="flex gap-3">
                  <Button type="button" variant="outline" onClick={() => setStep(1)} className="flex-1 rounded-lg h-11">Back</Button>
                  <Button type="button" onClick={() => setStep(3)} className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg h-11" data-testid="next-step-2">
                    Next: Certifications
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Certifications */}
            {step === 3 && (
              <div className="space-y-4">
                <div>
                  <Label>Certifications & Training</Label>
                  <p className="text-xs text-slate-500 mb-2">Type to search or add custom certifications</p>
                  <div className="relative">
                    <Input
                      value={certInput}
                      onChange={(e) => setCertInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCertification(certInput); } }}
                      placeholder="Type certification name..."
                      className="rounded-lg"
                      data-testid="cert-input"
                    />
                    {filteredSuggestions.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-40 overflow-y-auto" data-testid="cert-suggestions">
                        {filteredSuggestions.map((s, i) => (
                          <div key={`suggestion-${s}`} className="px-3 py-2 cursor-pointer hover:bg-blue-50 text-sm" onClick={() => addCertification(s)} data-testid={`cert-suggestion-${i}`}>
                            {s}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Selected certifications */}
                {formData.certifications.length > 0 && (
                  <div className="flex flex-wrap gap-2" data-testid="cert-tags">
                    {formData.certifications.map((cert, i) => (
                      <span key={`cert-${cert}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {cert}
                        <button type="button" onClick={() => removeCertification(cert)} className="hover:text-red-600 transition-colors">
                          <X size={12} weight="bold" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Quick-add common certs */}
                <div>
                  <Label className="text-xs text-slate-500 mb-2 block">Quick Add</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {availableCerts.map((cert) => (
                      <button key={`quick-${cert}`} type="button" onClick={() => addCertification(cert)}
                        className="px-2 py-1 text-[11px] rounded-full border border-slate-200 text-slate-600 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-all">
                        + {cert}
                      </button>
                    ))}
                  </div>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm" data-testid="register-error">{error}</div>
                )}

                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setStep(2)} className="flex-1 rounded-lg h-11">Back</Button>
                  <Button type="submit" disabled={loading} className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg h-11" data-testid="register-submit">
                    {loading ? 'Creating Account...' : 'Create Account'}
                  </Button>
                </div>
              </div>
            )}

            {step !== 3 && error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mt-4" data-testid="register-error">{error}</div>
            )}
          </form>

          <div className="mt-6 text-center text-sm text-slate-600">
            Already have an account?{' '}
            <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">Sign in here</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
